// Security utilities for NocRenk
// Input validation, sanitization, and security helpers

/**
 * Sanitize URL input to prevent XSS and injection attacks
 */
export const sanitizeUrl = (url: string): string => {
  if (!url || typeof url !== 'string') {
    return '';
  }
  
  // Remove any potentially dangerous characters
  const sanitized = url
    .trim()
    .replace(/[<>'"]/g, '') // Remove HTML/JS injection chars
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/data:/gi, '') // Remove data: protocol for URLs
    .replace(/vbscript:/gi, '') // Remove vbscript: protocol
    .replace(/file:/gi, ''); // Remove file: protocol
  
  // Validate URL format
  try {
    // Add protocol if missing
    let testUrl = sanitized;
    if (!testUrl.startsWith('http://') && !testUrl.startsWith('https://')) {
      testUrl = 'https://' + testUrl;
    }
    
    const urlObj = new URL(testUrl);
    
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return '';
    }
    
    return testUrl;
  } catch {
    return '';
  }
};

/**
 * Validate image file type and size
 */
export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  // Check file type
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Invalid file type. Please use JPG, PNG, GIF, or WebP images.'
    };
  }
  
  // Check file size (10MB limit)
  const maxSize = 10 * 1024 * 1024; // 10MB in bytes
  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'File size too large. Please use images smaller than 10MB.'
    };
  }
  
  return { valid: true };
};

/**
 * Sanitize filename for safe storage
 */
export const sanitizeFilename = (filename: string): string => {
  if (!filename || typeof filename !== 'string') {
    return 'untitled';
  }
  
  return filename
    .trim()
    .replace(/[^a-zA-Z0-9.-]/g, '_') // Replace special chars with underscore
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single
    .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
    .toLowerCase()
    .substring(0, 100); // Limit length
};

/**
 * Rate limiting for API calls
 */
class RateLimiter {
  private requests: Map<string, number[]> = new Map();
  private readonly maxRequests: number;
  private readonly timeWindow: number;
  
  constructor(maxRequests: number = 10, timeWindowMs: number = 60000) {
    this.maxRequests = maxRequests;
    this.timeWindow = timeWindowMs;
  }
  
  isAllowed(identifier: string): boolean {
    const now = Date.now();
    const requests = this.requests.get(identifier) || [];
    
    // Remove old requests outside time window
    const validRequests = requests.filter(time => now - time < this.timeWindow);
    
    // Check if under limit
    if (validRequests.length >= this.maxRequests) {
      return false;
    }
    
    // Add current request
    validRequests.push(now);
    this.requests.set(identifier, validRequests);
    
    return true;
  }
  
  getRemainingRequests(identifier: string): number {
    const now = Date.now();
    const requests = this.requests.get(identifier) || [];
    const validRequests = requests.filter(time => now - time < this.timeWindow);
    
    return Math.max(0, this.maxRequests - validRequests.length);
  }
  
  getResetTime(identifier: string): number {
    const requests = this.requests.get(identifier) || [];
    if (requests.length === 0) return 0;
    
    const oldestRequest = Math.min(...requests);
    return oldestRequest + this.timeWindow;
  }
}

// Global rate limiter instances
export const apiRateLimiter = new RateLimiter(30, 60000); // 30 requests per minute
export const imageRateLimiter = new RateLimiter(10, 60000); // 10 image uploads per minute
export const websiteRateLimiter = new RateLimiter(5, 60000); // 5 website captures per minute

/**
 * Content Security Policy violation reporter
 */
export const setupCSPReporting = (): void => {
  if (typeof window === 'undefined') return;
  
  document.addEventListener('securitypolicyviolation', (event) => {
    console.warn('CSP Violation:', {
      directive: event.violatedDirective,
      blockedURI: event.blockedURI,
      lineNumber: event.lineNumber,
      columnNumber: event.columnNumber,
      sourceFile: event.sourceFile
    });
    
    // In production, you might want to send this to an error reporting service
    // reportCSPViolation(event);
  });
};

/**
 * Secure local storage wrapper
 */
export class SecureStorage {
  private static readonly PREFIX = 'nocrenk_';
  
  static setItem(key: string, value: any): boolean {
    try {
      const sanitizedKey = this.sanitizeKey(key);
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(this.PREFIX + sanitizedKey, serializedValue);
      return true;
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
      return false;
    }
  }
  
  static getItem<T>(key: string, defaultValue?: T): T | null {
    try {
      const sanitizedKey = this.sanitizeKey(key);
      const item = localStorage.getItem(this.PREFIX + sanitizedKey);
      return item ? JSON.parse(item) : defaultValue || null;
    } catch (error) {
      console.error('Failed to read from localStorage:', error);
      return defaultValue || null;
    }
  }
  
  static removeItem(key: string): boolean {
    try {
      const sanitizedKey = this.sanitizeKey(key);
      localStorage.removeItem(this.PREFIX + sanitizedKey);
      return true;
    } catch (error) {
      console.error('Failed to remove from localStorage:', error);
      return false;
    }
  }
  
  static clear(): boolean {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.PREFIX)) {
          localStorage.removeItem(key);
        }
      });
      return true;
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
      return false;
    }
  }
  
  private static sanitizeKey(key: string): string {
    return key.replace(/[^a-zA-Z0-9_-]/g, '_');
  }
}

/**
 * Input validation helpers
 */
export const validators = {
  email: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },
  
  hexColor: (color: string): boolean => {
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexRegex.test(color);
  },
  
  hslColor: (color: string): boolean => {
    const hslRegex = /^hsl\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%\s*\)$/;
    return hslRegex.test(color);
  },
  
  url: (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  },
  
  filename: (filename: string): boolean => {
    const filenameRegex = /^[a-zA-Z0-9._-]+$/;
    return filenameRegex.test(filename) && filename.length <= 255;
  }
};

/**
 * Error boundary helper for security-related errors
 */
export const handleSecurityError = (error: Error, context: string): void => {
  console.error(`Security error in ${context}:`, error);
  
  // In production, report to error monitoring service
  // reportSecurityError(error, context);
  
  // Show user-friendly message
  if (error.message.includes('CSP') || error.message.includes('CORS')) {
    console.warn('Security policy blocked request. This is normal for external resources.');
  }
};

/**
 * Initialize security features
 */
export const initializeSecurity = (): void => {
  setupCSPReporting();
  
  // Disable right-click context menu in production (optional)
  if (import.meta.env.PROD) {
    document.addEventListener('contextmenu', (e) => {
      // Allow context menu for development
      // e.preventDefault();
    });
  }
  
  // Disable text selection for sensitive areas (optional)
  // document.addEventListener('selectstart', (e) => {
  //   if (e.target.classList.contains('no-select')) {
  //     e.preventDefault();
  //   }
  // });
  
  console.log('Security features initialized');
};